import React, { useState } from 'react';
import { useAgent } from '../../context/AgentContext';
import { X, Bot, Briefcase, Users, Scale, ShieldCheck, Settings2 } from 'lucide-react';
import './CreateAgentModal.css';

const agentTypes = [
  {
    id: 'general',
    name: 'General Assistant',
    icon: Bot,
    color: '#3B82F6',
    description: 'A versatile AI assistant for general document analysis and Q&A'
  },
  {
    id: 'finance',
    name: 'Finance Agent',
    icon: Briefcase,
    color: '#10B981',
    description: 'Specialized in financial documents, reports, and analysis'
  },
  {
    id: 'hr',
    name: 'HR Agent',
    icon: Users,
    color: '#8B5CF6',
    description: 'Expert in HR policies, employee documentation, and procedures'
  },
  {
    id: 'legal',
    name: 'Legal Agent',
    icon: Scale,
    color: '#F59E0B',
    description: 'Analyzes contracts, legal documents, and compliance issues'
  },
  {
    id: 'compliance',
    name: 'Compliance Agent',
    icon: ShieldCheck,
    color: '#EF4444',
    description: 'Focuses on regulatory compliance and risk assessment'
  },
  {
    id: 'custom',
    name: 'Custom Agent',
    icon: Settings2,
    color: '#6B7280',
    description: 'Create a custom agent with your own instructions'
  }
];

const CreateAgentModal = ({ onClose }) => {
  const { createAgent, selectAgent } = useAgent();
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setFormData({
      ...formData,
      name: type.id === 'custom' ? '' : `My ${type.name}`
    });
  };

  const handleNext = () => {
    if (selectedType) {
      setStep(2);
    }
  };

  const handleBack = () => {
    setStep(1);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Please enter a name for your agent');
      return;
    }

    setLoading(true);
    try {
      const agent = await createAgent({
        name: formData.name.trim(),
        description: formData.description.trim(),
        type: selectedType.id,
        color: selectedType.color
      });

      // Select the newly created agent
      selectAgent(agent);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create agent');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal create-agent-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {step === 1 ? 'Create New Agent' : 'Configure Agent'}
          </h2>
          <button className="btn btn-icon btn-ghost" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {step === 1 ? (
            <div className="agent-type-selection">
              <p className="selection-hint">Choose an agent type to get started:</p>
              <div className="agent-types-grid">
                {agentTypes.map((type) => (
                  <button
                    key={type.id}
                    className={`agent-type-card ${selectedType?.id === type.id ? 'selected' : ''}`}
                    onClick={() => handleTypeSelect(type)}
                    style={{ '--agent-color': type.color }}
                  >
                    <div className="agent-type-icon" style={{ backgroundColor: type.color }}>
                      <type.icon size={24} />
                    </div>
                    <div className="agent-type-info">
                      <h3>{type.name}</h3>
                      <p>{type.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <form className="agent-config-form" onSubmit={handleSubmit}>
              {error && (
                <div className="form-error">{error}</div>
              )}

              <div className="selected-type-preview">
                <div
                  className="type-preview-icon"
                  style={{ backgroundColor: selectedType.color }}
                >
                  <selectedType.icon size={24} />
                </div>
                <div className="type-preview-info">
                  <span className="type-preview-name">{selectedType.name}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    onClick={handleBack}
                  >
                    Change type
                  </button>
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="agent-name">Agent Name</label>
                <input
                  type="text"
                  id="agent-name"
                  className="input"
                  placeholder="Enter a name for your agent"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  autoFocus
                />
              </div>

              <div className="input-group">
                <label htmlFor="agent-description">Description (optional)</label>
                <textarea
                  id="agent-description"
                  className="input"
                  placeholder="Describe what this agent will be used for..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
            </form>
          )}
        </div>

        <div className="modal-footer">
          {step === 1 ? (
            <>
              <button className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleNext}
                disabled={!selectedType}
              >
                Next
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={handleBack}>
                Back
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={loading || !formData.name.trim()}
              >
                {loading ? <span className="spinner" /> : 'Create Agent'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateAgentModal;
